import { create } from "zustand";
import type { QuoteRow } from "@/lib/quote-cache";

interface LiveQuotesState {
  byTicker: Map<string, QuoteRow[]>;
  triggeredAlertIds: Set<string>;
  setRowsForTicker: (ticker: string, rows: QuoteRow[]) => void;
  appendRowsForTicker: (ticker: string, rows: QuoteRow[]) => void;
  clearTicker: (ticker: string) => void;
  setTriggeredAlerts: (ids: Set<string>) => void;
  addTriggeredAlert: (id: string) => void;
}

export const useLiveQuotesStore = create<LiveQuotesState>((set) => ({
  byTicker: new Map(),
  triggeredAlertIds: new Set(),
  setRowsForTicker: (ticker, rows) =>
    set((s) => {
      const next = new Map(s.byTicker);
      next.set(ticker.toUpperCase(), rows);
      return { byTicker: next };
    }),
  appendRowsForTicker: (ticker, rows) =>
    set((s) => {
      const key = ticker.toUpperCase();
      const seen = new Map<string, QuoteRow>();
      const existing = s.byTicker.get(key) ?? [];
      for (const r of existing) seen.set(r.timestamp, r);
      for (const r of rows) seen.set(r.timestamp, r);
      const merged = Array.from(seen.values()).sort((a, b) =>
        a.timestamp.localeCompare(b.timestamp),
      );
      const next = new Map(s.byTicker);
      next.set(key, merged);
      return { byTicker: next };
    }),
  clearTicker: (ticker) =>
    set((s) => {
      const key = ticker.toUpperCase();
      if (!s.byTicker.has(key)) return s;
      const next = new Map(s.byTicker);
      next.delete(key);
      return { byTicker: next };
    }),
  setTriggeredAlerts: (ids) => set({ triggeredAlertIds: ids }),
  addTriggeredAlert: (id) =>
    set((s) => {
      if (s.triggeredAlertIds.has(id)) return s;
      const next = new Set(s.triggeredAlertIds);
      next.add(id);
      return { triggeredAlertIds: next };
    }),
}));

const EMPTY_ROWS: QuoteRow[] = Object.freeze([]) as unknown as QuoteRow[];

export const useTickerRows = (ticker: string): QuoteRow[] =>
  useLiveQuotesStore((s) => s.byTicker.get(ticker.toUpperCase()) ?? EMPTY_ROWS);

export const useLatestPriceForTicker = (ticker: string): number | null =>
  useLiveQuotesStore((s) => {
    const rows = s.byTicker.get(ticker.toUpperCase());
    if (!rows || rows.length === 0) return null;
    return rows[rows.length - 1].price;
  });

export const useTriggeredAlerts = () =>
  useLiveQuotesStore((s) => s.triggeredAlertIds);

export function latestRowFor(
  state: { byTicker: Map<string, QuoteRow[]> },
  ticker: string,
): QuoteRow | null {
  const rows = state.byTicker.get(ticker.toUpperCase());
  if (!rows || rows.length === 0) return null;
  return rows[rows.length - 1];
}

export function latestPriceFromState(
  state: { byTicker: Map<string, QuoteRow[]> },
  ticker: string,
): number | null {
  return latestRowFor(state, ticker)?.price ?? null;
}

export type { QuoteRow as LiveQuoteRow } from "@/lib/quote-cache";
